import _ from 'lodash';
import type { Messages } from '@common/messages';
import EnvironmentConfig from '../config-environment';

/** used to help know if the id for team or org is a public id which is a 12 character string or our internal uuid */
export const PUBLIC_ID_LENGTH = 12;
export const CONSTANT_ID_LENGTH = 36;

const isPublic = (route: string) => route.includes('/p/');

export const PUBLIC_PREFIX = EnvironmentConfig.PUBLIC_PREFIX;

// From: https://stackoverflow.com/a/71353081
type Replace<
    T extends string,
    S extends string,
    D extends string,
    A extends string = '',
> = T extends `${infer L}${S}${infer R}` ? Replace<R, S, D, `${A}${L}${D}`> : `${A}${T}`;

export type TeamRoute = `${string}:teamID${string}`;
export type TeamRoutes<T extends TeamRoute> = ReturnType<typeof teamRoutes<T>>;

function teamRoutes<Path extends TeamRoute>(path: Path) {
    return {
        id: path,
        named: path.replace(':teamID', ':teamID/:teamName') as Replace<Path, ':teamID', ':teamID/:teamName'>,
    };
}

export function teamRoutesFrom<T1 extends string, T2 extends String>({ id, named }: { id: T1; named: T2 }): [T1, T2] {
    return [id, named];
}

export const ROUTES = {
    home: '/',
    homePage: '/home',
    athleteProfile: '/athlete/:handle',
    topLevelOrgs: {
        create: '/create-org',
        home: '/orgs/:id',
    },
    teams: {
        create: '/create-team',
        list: '/teams',
        teamPass: teamRoutes(`/teams/:teamID/team-pass`),
        homePage: teamRoutes('/teams/:teamID'),
        homePageFollow: teamRoutes('/teams/:teamID/follow'),
        stats: teamRoutes('/teams/:teamID/season-stats'),
        teamInfo: teamRoutes('/teams/:teamID/team'),
        addPlayer: teamRoutes('/teams/:teamID/team/add-player'),
        addStaff: teamRoutes('/teams/:teamID/team/add-staff'),
        playerProfile: teamRoutes('/teams/:teamID/players/:playerID'),
        playerProfileClip: teamRoutes('/teams/:teamID/players/:playerID/clips/:videoID'),
        events: {
            event: teamRoutes('/teams/:teamID/events/:eventID'),
            eventShareDownload: teamRoutes('/teams/:teamID/events/:eventID/game-stream'),
            schedule: teamRoutes('/teams/:teamID/schedule'),
            scheduleEvent: teamRoutes('/teams/:teamID/schedule/:eventID'),
            eventInfo: teamRoutes('/teams/:teamID/schedule/:eventID/info'),
            addEvent: teamRoutes('/teams/:teamID/schedule/add-event'),
            gameStats: teamRoutes('/teams/:teamID/schedule/:eventID/game-stats'),
            startingLineup: teamRoutes('/teams/:teamID/schedule/:eventID/starting-lineup'),
            boxScore: teamRoutes('/teams/:teamID/schedule/:eventID/box-score'),
            videos: teamRoutes('/teams/:teamID/schedule/:eventID/videos'),
            video: teamRoutes('/teams/:teamID/schedule/:eventID/videos/:videoID'),
            videoClips: teamRoutes('/teams/:teamID/schedule/:eventID/videos/clips'),
            videoClip: teamRoutes('/teams/:teamID/schedule/:eventID/videos/clips/:videoID'),
            plays: teamRoutes('/teams/:teamID/schedule/:eventID/plays'),
            recap: teamRoutes('/teams/:teamID/schedule/:eventID/recap'),
            insights: teamRoutes('/teams/:teamID/schedule/:eventID/insights'),
            highlightReel: teamRoutes('/teams/:teamID/schedule/:eventID/recap/reels'),
        },
        opponents: teamRoutes('/teams/:teamID/opponents'),
        opponentInfo: {
            roster: teamRoutes('/teams/:teamID/opponents/:opponentID/roster'),
            rosterAddPlayer: teamRoutes('/teams/:teamID/opponents/:opponentID/roster/add-player'),
        },
        tools: teamRoutes('/teams/:teamID/tools'),
        announcements: teamRoutes('/teams/:teamID/announcements'),
    },
    organizations: {
        join: '/organizations/:orgID/join',
        events: {
            eventInfo: '/organizations/:orgID/schedule/:eventID/info',
            editEvent: '/organizations/:orgID/schedule/:eventID/edit',
            event: '/organizations/:orgID/schedule/:eventID',
            boxScore: '/organizations/:orgID/schedule/:eventID/box-score',
            plays: '/organizations/:orgID/schedule/:eventID/plays',
            schedule: '/organizations/:orgID/schedule',
            bulkImport: '/organizations/:orgID/schedule/bulk-import',
            bulkCsvUpload: '/organizations/:orgID/schedule/bulk-csv-upload',
            delete: '/organizations/:orgID/schedule/bulk-delete',
            recap: '/organizations/:orgID/schedule/:eventID/recap',
        },
        homePage: '/organizations/:orgID/home',
        home: '/organizations/:orgID',
        leaders: {
            list: '/organizations/:orgID/leaders',
            stats: '/organizations/:orgID/leaders/stats',
        },
        teams: '/organizations/:orgID/teams',
        standings: '/organizations/:orgID/standings',
        tools: '/organizations/:orgID/tools',
        announcements: '/organizations/:orgID/announcements',
    },
    subscriptions: {
        create: '/subscription/create/:planCode',
        upgrade: '/subscription/upgrade/:planCode',
        switch: '/subscription/switch/:planCode',
        downgrade: '/subscription/downgrade/:planCode',
        trial: '/subscription/trial/:planCode',
        checkout: '/subscription/checkout/:planCode',
        success: '/subscription/success',
    },
    account: {
        home: '/account',
        subscription: '/account/subscription',
        details: '/account/details',
        family: '/account/family',
    },
    login: '/login',
    email: '/email',
    onboarding: {
        persona: '/persona',
        welcome: '/welcome',
        start: '/start',
    },
    pricing: '/pricing',
    search: '/search',
    createAccount: '/create-account',
    checkYourEmail: '/check-your-email',
    resetPassword: '/reset-password',
    tokenLogin: '/token-login',
    ccpa: '/ccpa',
    eventRedirect: '/events/:eventID',
    announcement: {
        view: '/announcements/:announcementID',
        list: '/announcements',
    },
} as const;

/**
 * Team search entered from onboarding. Kept out of ROUTES because every value in there must be a
 * matchable path pattern — `regexify(ROUTES)` in the Playwright helpers throws on a query string.
 */
export const ONBOARDING_SEARCH_URL = `${ROUTES.search}?context=onboarding`;

/**
 * Routes that are only available in non-production environments.
 * These are intentionally separated from ROUTES to prevent accidental use in production code.
 */
export const NON_PRODUCTION_ROUTES = {
    styleGuide: '/style-guide',
    appInfo: '/app-info',
} as const;

export const DEPRECATED_ROUTES = {
    teamInfo: teamRoutes('/teams/:teamID/teamInfo'),
    teams: {
        live: teamRoutes('/teams/:teamID/live'),
        followTeam: teamRoutes('/teams/:teamID/live/follow'),
    },
    public: {
        teams: {
            team: teamRoutes('/p/teams/:teamID'),
            live: '/p/teams/:teamID/:teamName/live',
            followTeam: '/p/teams/:teamID/:teamName/follow',
        },
    },
} as const;

export const DEEP_LINKS = {
    claimClassicAccount: '/claim/*',
} as const;

/**
 * These are unsupported routes on web, but they exist as deep links on native.
 */
export const UNSUPPORTED_ROUTES = {
    teams: {
        practiceScoring: '/teams/:teamID/settings/practice-scoring',
    },
    events: {
        addEvent: '/events/add-event',
    },
    home: '/f/uni/',
};

export const all_routes = (routes: object): string[] =>
    _.flatMap(routes, (r) => (typeof r === 'object' ? all_routes(r) : r));

export const ROUTES_WITHIN_AUTH_FLOWS: string[] = [ROUTES.home, ROUTES.login, ROUTES.email, ROUTES.resetPassword];

export const ROUTES_WITHOUT_LOGIN: string[] = all_routes(ROUTES)
    .filter((route) => [/\/teams\/(.*)\/events\/(.*)/, /\/player-profile/].some((re) => re.test(route)))
    .filter((route) => !route.includes('/game-stream'));

export const ROUTES_WITH_BACK_TO_SCHEDULE_HEADER: string[] = [...all_routes(ROUTES)].filter(
    (r) => r.includes('/schedule/') && !r.endsWith('/add-event'),
);

export const ROUTES_WITH_BACK_TO_LEADERS_HEADER: string[] = [...all_routes(ROUTES)].filter(
    (r) => r.includes('/leaders/') && !r.endsWith('/leaders') && !r.endsWith('/leaders/'),
);

export const ROUTES_WITH_BACK_TO_OPPONENT_HEADER: string[] = all_routes(ROUTES).filter((r) =>
    r.includes('/opponents/'),
);

export const ROUTES_WITH_BACK_TO_SEARCH_HEADER: string[] = [
    ROUTES.teams.homePage.id,
    ROUTES.teams.homePage.named,
    ROUTES.organizations.homePage,
    ROUTES.organizations.events.schedule,
];

export const ROUTES_WITH_BACK_TO_ACCOUNT: string[] = [
    ROUTES.account.details,
    ROUTES.account.subscription,
    ROUTES.account.family,
];
export const ROUTES_WITH_BACK_TO_PRICING: string[] = [
    ROUTES.subscriptions.create,
    ROUTES.subscriptions.upgrade,
    ROUTES.subscriptions.switch,
    ROUTES.subscriptions.downgrade,
    ROUTES.subscriptions.trial,
    ROUTES.subscriptions.checkout,
];
export const ROUTES_WITH_BACK_TO_ORG_HOME: string[] = [ROUTES.organizations.announcements];
export const ROUTES_WITH_BACK_TO_TEAM_HOME: string[] = [ROUTES.teams.announcements.id];
export const ROUTES_WITH_BACK_TO_ANNOUNCEMENTS: string[] = [ROUTES.announcement.view];

export const ROUTES_WITH_BACK_NAVIGATION: string[] = [...all_routes(ROUTES)].filter(
    (r) =>
        ROUTES_WITH_BACK_TO_ANNOUNCEMENTS.includes(r) ||
        ROUTES_WITH_BACK_TO_OPPONENT_HEADER.includes(r) ||
        ROUTES_WITH_BACK_TO_SCHEDULE_HEADER.includes(r) ||
        ROUTES_WITH_BACK_TO_LEADERS_HEADER.includes(r) ||
        ROUTES_WITH_BACK_TO_ACCOUNT.includes(r) ||
        ROUTES_WITH_BACK_TO_PRICING.includes(r) ||
        ROUTES_WITH_BACK_TO_ORG_HOME.includes(r) ||
        ROUTES_WITH_BACK_TO_TEAM_HOME.includes(r),
);
const ROUTES_NOT_USING_BACK_TO_SCHEDULE: string[] = all_routes(ROUTES).filter(
    (r) => !ROUTES_WITH_BACK_TO_SCHEDULE_HEADER.includes(r),
);

export const ROUTES_WITH_TEAMS: string[] = all_routes(ROUTES).filter((r) => r.includes('/teams') && !isPublic(r));
export const ROUTES_WITH_ORGS: string[] = all_routes(ROUTES).filter((r) => r.includes('/organizations'));

export const ROUTES_WITH_TEAM_NAV = ROUTES_NOT_USING_BACK_TO_SCHEDULE;
export const ROUTES_WITH_ORG_NAV = ROUTES_WITH_ORGS;

export const ROUTES_WITH_EVENT_ID: string[] = all_routes(ROUTES).filter(
    (r) => !r.includes('add-event') && r.includes(':eventID'),
);

export function generateLoginUrl({
    redirectTo,
    withPrefix,
    messageId,
}: {
    redirectTo: string;
    withPrefix: boolean;
    messageId?: keyof typeof Messages;
}) {
    const params = new URLSearchParams();
    params.append('redirect', redirectTo);
    if (messageId) {
        params.append('messageId', messageId);
    }
    const prefix = withPrefix ? PUBLIC_PREFIX : '';
    return `${prefix}${ROUTES.login}?${params.toString()}`;
}

export function buildPathWithRedirect(path: string, redirectUrl?: string): string {
    return redirectUrl ? `${path}?${new URLSearchParams({ redirect: redirectUrl })}` : path;
}

/**
 * Reduces a redirect URL to its pathname so it can be compared against route patterns.
 * `isSafeRedirectUrl` accepts relative paths, query strings and absolute gc.com URLs, but
 * `matchPath` only matches bare pathnames.
 */
export function toRoutePathname(url: string): string {
    return new URL(url, window.location.origin).pathname;
}
